const AUTH_STATE_CHANNEL = "auth_state_channel";
const AUTH_EVENT = {
  STATE_CHANGE: "stateChange",
} as const;
const STATE_CHANGE_ACTION = {
  LOGOUT: "logout",
  SWITCH_ACCOUNT: "switchAccount",
} as const;

type AuthEvent = (typeof AUTH_EVENT)[keyof typeof AUTH_EVENT];
type StateChangeAction = (typeof STATE_CHANGE_ACTION)[keyof typeof STATE_CHANGE_ACTION];
type EventCallback = (data) => void;

class AuthStateChannel {
  private static instance: AuthStateChannel;
  private channel: BroadcastChannel;
  private listeners = new Map<AuthEvent, Set<EventCallback>>();

  private constructor() {
    this.channel = new BroadcastChannel(AUTH_STATE_CHANNEL);
    this.channel.onmessage = this.handleMessage.bind(this);
  }

  // Singleton access
  public static getInstance(): AuthStateChannel {
    if (!AuthStateChannel.instance) {
      AuthStateChannel.instance = new AuthStateChannel();
    }
    return AuthStateChannel.instance;
  }

  // Add event listener
  public on(event: AuthEvent, callback: EventCallback): void {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event)!.add(callback);
  }

  // Send state change event to other tabs
  public sendStateChange(action: StateChangeAction): void {
    this.channel.postMessage({ event: AUTH_EVENT.STATE_CHANGE, data: action });
  }

  public logout(): void {
    this.sendStateChange(STATE_CHANGE_ACTION.LOGOUT);
  }

  public switchAccount(): void {
    this.sendStateChange(STATE_CHANGE_ACTION.SWITCH_ACCOUNT);
  }

  // Internal message handler
  private handleMessage(event: MessageEvent): void {
    const { event: eventName, data } = event.data || {};
    if (!eventName) return;

    const callbacks = this.listeners.get(eventName as AuthEvent);
    callbacks?.forEach((cb) => cb(data));
  }

  public close(): void {
    this.channel.close();
    this.listeners.clear();
  }
}

// Export a shared singleton instance
export const authStateChannel = AuthStateChannel.getInstance();
export { STATE_CHANGE_ACTION };
